import * as Sentry from '@sentry/react'
import {measure, reportWorkerTimings, checkSlow} from './perf'
import {tickOperation} from './session-context'

const _getStageContext = (stage) => {
  const children = stage.find('*') || []
  return {
    layer_count: children.filter((n) => n.nodeType === 'Shape' || n.nodeType === 'Group').length,
    image_layer_count: children.filter((n) => n.className === 'Image').length,
    text_layer_count: children.filter((n) => n.className === 'Text').length,
  }
}

/** Create Konva drag/transform handlers with Sentry tracing. */
export const makeKonvaHandlers = (baseContext = {}) => {
  let dragStart = 0
  let transformStart = 0

  const _tags = (node, ctx) => ({
    element_type: node.className === 'Image' ? 'image' : node.className === 'Text' ? 'text' : 'shape',
    layer_count: ctx.layer_count, image_layer_count: ctx.image_layer_count,
    has_filter: String(Boolean(node.filters && (node.filters() || []).length > 0)),
    ...baseContext,
  })

  const _end = (name, op, startTime, e) => {
    const endMs = performance.now()
    const duration = endMs - startTime
    const node = e.target
    const stage = node.getStage()
    if (!stage) return
    const tags = _tags(node, _getStageContext(stage))
    const endSentry = Date.now() / 1000
    const startSentry = endSentry - duration / 1000
    const tx = Sentry.startTransaction({name, op, startTimestamp: startSentry})
    Object.entries(tags).forEach(([k, v]) => tx.setTag(k, String(v)))
    tx.setMeasurement('duration_ms', duration, 'millisecond')
    tx.setStatus('ok')
    tx.finish(endSentry)
    checkSlow(name, duration, tags)
    tickOperation()
  }

  return {
    onDragStart: () => { dragStart = performance.now() },
    onDragEnd: (e) => _end('konva.drag', 'ui.drag', dragStart, e),
    onTransformStart: () => { transformStart = performance.now() },
    onTransformEnd: (e) => _end('konva.transform', 'ui.transform', transformStart, e),
  }
}

/** Capture Konva stage as data URL, hiding Transformer during export. */
export const captureStage = (stage, opts = {}) => {
  const {purpose = 'preview', pixelRatio = 1, mimeType = 'image/png', quality = 0.92} = opts
  const stageCtx = _getStageContext(stage)
  const megapixels = (stage.width() * stage.height() * pixelRatio * pixelRatio) / 1_000_000
  const tags = {purpose, pixel_ratio: pixelRatio, has_offscreen_worker: 'false', ...stageCtx}
  const measurements = {total_megapixels: megapixels, ...stageCtx}

  return measure('canvas.capture', 'canvas.capture', () => {
    const trs = stage.find('Transformer')
    trs.forEach((tr) => tr.visible(false))
    stage.batchDraw()
    const dataUrl = stage.toDataURL({pixelRatio, mimeType, quality})
    trs.forEach((tr) => tr.visible(true))
    stage.batchDraw()
    const kb = Math.round((dataUrl.length * 3) / 4 / 1024)
    tags.output_kb = kb
    measurements.output_kb = kb
    return dataUrl
  }, {tags, measurements})
}

/** Capture imageData via web worker using OffscreenCanvas. Returns ArrayBuffer of the image */
export const captureViaWorker = (imageData, opts = {}) => {
  const worker = new window.Worker(new URL('../webworkers/canvas-capture.worker.js', import.meta.url))
  return new Promise((resolve, reject) => {
    let workerStart = 0
    worker.onmessage = ({data}) => {
      if (data.type === 'DONE') {
        const workerEnd = performance.now()
        const output_kb = Math.round(data.buffer.byteLength / 1024)
        reportWorkerTimings(data.timings, {
          tags: { purpose: opts.purpose || 'unknown', pixel_ratio: opts.pixelRatio || 1, has_offscreen_worker: 'true' },
          measurements: { output_kb },
          startTimestamp: workerStart,
          endTimestamp: workerEnd,
        })
        resolve(data.buffer)
      } else {
        reject(new Error(data.error))
      }
      worker.terminate()
    }
    workerStart = performance.now()
    worker.postMessage({imageData, ...opts}, [imageData.data.buffer])
  })
}
